import { withDefaults } from '../../../core/theme';
import { parseZoneKey } from '../../../core/field/zones';
import { OBJECT_STYLE } from '../drawObjects';

/**
 * **カードの出どころ**（Issue #561 → #589 で `fx/move/enter.js` から切り出し）。場に出る（`effects/move_enter_motion.js`）と
 * 発動（`effects/chaining.js`）が同じ規則で「どこから飛んでくるか」を決める。
 */
const FIELD = ['monster', 'magic_trap', 'field'];

const v = withDefaults(() => [OBJECT_STYLE]);

/** 盤のカードの上面の高さ（`drawObjects.js::card` と同じ規則） */
export function topOf(zone, c, style) {
  const s = v(style);
  const monster = parseZoneKey(zone)?.kind === 'monster';
  return (monster && c.faceUp && !c.defense ? s.attackLift : s.cardLift) + s.cardThickness;
}

/** ゾーンの中心（平面の座標。`ObjectLayer.js::zoneCenter` と同じ） */
export function zoneCenter(field, zone) {
  const q = zone ? field?.zonePlane?.(zone) : null;
  if (!Array.isArray(q) || q.length !== 4) return null;
  const cx = q.reduce((a, p) => a + p[0], 0) / 4;
  const cy = q.reduce((a, p) => a + p[1], 0) / 4;
  return Number.isFinite(cx) && Number.isFinite(cy) ? [cx, cy] : null;
}

/**
 * EX デッキの場所が分からない（実写の Field は EX デッキを知らない）ときの見当:
 * マットの EX デッキは魔法・罠の列の、デッキと反対側の端にある ＝ 魔法・罠の中央を挟んでデッキと向かい合う点
 */
function extraGuess(field, player) {
  const deck = zoneCenter(field, `maindeck:${player}`);
  const mid = zoneCenter(field, `magic_trap:${player}:3`);
  return deck && mid ? [2 * mid[0] - deck[0], 2 * mid[1] - deck[1]] : null;
}


/**
 * **場所の種類 → HUD の部品の名前と、その中心の取り方**（出どころが盤に無い・画面の外のときに使う）。
 * climax の HUD に今ある部品だけ（`drawHud.js::anchors`。`hand` は x・y とも中心）。
 * 墓地・EX デッキ・デッキなどの部品を HUD に足したら、ここに 1 行足せば同じ仕組みで引ける
 */
export const HUD_SOURCES = Object.freeze({
  hand: { part: 'hand', center: (b) => [b.x, b.y] },
});

/** 舞台のピクセルの点が画面の内側か */
const onScreen = (pt, canvas) => Boolean(pt) && Number.isFinite(pt[0]) && Number.isFinite(pt[1])
  && pt[0] >= 0 && pt[0] <= canvas.w && pt[1] >= 0 && pt[1] <= canvas.h;

/** 舞台のピクセルの点 → 盤の平面の点（地平線の向こうなら下へずらして探す）。見つからなければ null */
function planeUnder(f, [x, y0], H) {
  for (let y = y0; y < H; y += 40) {
    const q = f?.toPlane?.([x, y]);
    if (q) return q;
  }
  return null;
}

/**
 * **出どころ（ワールドの座標）**。人が決めた順（ユーザー 2026-09-28）:
 *
 * 1. 盤（Field）にそのゾーンがあり、**画面の中に見えている**ならそこ（EX デッキは見当 `extraGuess` もここ。
 *    手札も Field に手札のゾーンがあれば同じ）
 * 2. 無い・画面の外なら、**HUD にその場所の部品があれば**そこ（`HUD_SOURCES`）を盤の平面へ戻した点
 * 3. それも無ければ、**画面の上の外から降ってくる**（行き先の真上の、画面の上端より外）
 *
 * @returns {{ world: number[], via: 'field' | 'hud' | 'above' }}
 */
export function sourceOf(ctx, plane, end) {
  const { payload } = ctx;
  const f = ctx.stage?.field;
  const canvas = ctx.stage?.canvas ?? { w: 1920, h: 1080 };
  const from = parseZoneKey(payload?.fromZone);
  const toWorld = ([u, w], y) => [u - plane.w / 2, y, w - plane.h / 2];
  // 1. 盤のゾーン（画面の中に見えているときだけ）
  if (from) {
    const c = zoneCenter(f, payload.fromZone) ?? (from.kind === 'extra' ? extraGuess(f, from.player) : null);
    if (c && onScreen(f?.toStage?.(c), canvas)) {
      const s = v(ctx.style);
      const y = FIELD.includes(from.kind) ? topOf(payload.fromZone, { faceUp: payload.prev?.faceUp !== false, defense: payload.prev?.defense }, ctx.style) : s.pileMax * 0.3;
      return { world: toWorld(c, y), via: 'field' };
    }
  }
  // 2. HUD の部品
  const hud = from ? HUD_SOURCES[from.kind] : null;
  const box = hud ? ctx.stage?.hud?.[hud.part]?.(from.player) : null;
  if (box) {
    const q = planeUnder(f, hud.center(box), canvas.h);
    if (q) return { world: toWorld(q, 0), via: 'hud' };
  }
  // 3. 画面の上の外から降ってくる（行き先の真上。地平線の向こうなら行き先の上の高いところ）
  const dest = f?.toStage?.([end[0] + plane.w / 2, end[2] + plane.h / 2]);
  const x = dest?.[0] ?? canvas.w / 2;
  // 画面の上端のすぐ外（遠すぎると HUD の下を通っている間に尺の大半を使ってしまう）
  for (let y = -60; y < 0; y += 20) {
    const q = f?.toPlane?.([x, y]);
    if (q) return { world: toWorld(q, 0), via: 'above' };
  }
  return { world: [end[0], end[1] + plane.h, end[2]], via: 'above' };
}
